/* Public customer photo-upload page. Mobile-first. NO auth required —
   access is gated by the secret token in the URL. */
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "../lib/api";

export default function CustomerUploadPortal() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const r = await fetch(`${API}/public/upload/${token}/info`);
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setError(j.detail || "This link is no longer valid.");
        setInfo(null);
        return;
      }
      const j = await r.json();
      setInfo(j);
    } catch (e) {
      setError("Couldn't reach the server. Please check your connection and try again.");
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      const r = await fetch(`${API}/public/upload/${token}/upload`, { method: "POST", body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setError(j.detail || "Upload failed."); }
      await load();
    } catch (e) { setError("Upload failed. Please try again."); }
    finally { setBusy(false); }
  };

  return (
    <div style={pageStyle} data-testid="customer-upload-portal">
      <div style={cardStyle}>
        <div style={brandStyle}>
          <div style={{ fontWeight: 800, fontSize: 22, color: "#061426" }}>Scott Sells Chevys</div>
          <div style={{ fontSize: 11, letterSpacing: ".06em", color: "#475569", textTransform: "uppercase" }}>
            Byers Chevrolet · Secure Document Upload
          </div>
        </div>

        {error && (
          <div style={{ background: "#fee2e2", color: "#991b1b", padding: 12, borderRadius: 8, marginBottom: 12 }} data-testid="portal-error">
            {error}
          </div>
        )}

        {!info && !error && <div style={{ textAlign: "center", color: "#64748b", padding: 30 }}>Loading…</div>}

        {info && (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: "#061426", margin: "8px 0 4px" }}>
              Hi {info.first_name}!
            </h1>
            <p style={{ color: "#475569", lineHeight: 1.45, fontSize: 14, margin: "0 0 14px" }}>
              {info.note_to_customer ||
                "Scott from Byers Chevy here. Tap each box below to upload a quick photo from your phone. This link is secure and only Scott's team can see your photos."}
            </p>

            {info.items.map(item => (
              <UploadRow key={item.kind} item={item} busy={busy} onUpload={(f) => upload(item.kind, f)} />
            ))}

            {info.items.every(i => i.uploaded) && (
              <div style={{ background: "#dcfce7", color: "#166534", padding: 14, borderRadius: 8, marginTop: 12, textAlign: "center" }} data-testid="portal-done">
                <div style={{ fontWeight: 700, fontSize: 18 }}>All set, {info.first_name}!</div>
                <div style={{ fontSize: 13, marginTop: 4 }}>Scott has been notified. You can close this page.</div>
              </div>
            )}

            <div style={{ marginTop: 18, fontSize: 11, color: "#94a3b8", textAlign: "center", lineHeight: 1.5 }}>
              This link is unique to you and stops working {info.expires_at ? `on ${new Date(info.expires_at).toLocaleString()}` : "shortly"}.
              <br />Your photos are stored privately and never shared outside our dealership.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function UploadRow({ item, busy, onUpload }) {
  const inputRef = useRef(null);
  const [pending, setPending] = useState(null);
  const handleChoose = (e) => {
    const f = e.target.files?.[0];
    if (f) { setPending(f); onUpload(f); }
  };
  return (
    <div style={{
      border: "1px solid #cbd5e1", borderRadius: 12, padding: 14, marginBottom: 10,
      background: item.uploaded ? "#f0fdf4" : "#fff",
    }} data-testid={`portal-item-${item.kind}`}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: "#061426", fontSize: 15 }}>{item.label}</div>
          <div style={{ fontSize: 12, color: item.uploaded ? "#15803d" : "#64748b", marginTop: 2 }}>
            {item.uploaded ? "✓ Received" : "Tap below to upload"}
          </div>
        </div>
        <button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          style={{
            background: item.uploaded ? "#15803d" : "#006DFF",
            color: "#fff", border: 0, borderRadius: 10, padding: "10px 16px",
            fontWeight: 700, fontSize: 14, cursor: "pointer", minWidth: 110,
          }}
          data-testid={`portal-btn-${item.kind}`}
        >
          {item.uploaded ? "Replace" : busy && pending ? "Uploading…" : "Take Photo"}
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" capture="environment"
        onChange={handleChoose} style={{ display: "none" }}
        data-testid={`portal-input-${item.kind}`} />
    </div>
  );
}

const pageStyle = {
  minHeight: "100vh", background: "#f1f5f9",
  display: "flex", alignItems: "flex-start", justifyContent: "center",
  padding: "20px 14px", fontFamily: "'Helvetica Neue', Arial, sans-serif",
};
const cardStyle = {
  width: "100%", maxWidth: 480, background: "#fff", borderRadius: 14,
  padding: 22, boxShadow: "0 12px 30px rgba(2,6,23,.08)",
};
const brandStyle = {
  borderBottom: "3px solid #006DFF", paddingBottom: 10, marginBottom: 14,
};
